import { ExternalLinkIcon } from '@chakra-ui/icons';
import { Link, useColorModeValue } from '@chakra-ui/react';
import { Link as RouterLink } from 'react-router-dom';

/**
 * HelpLink component - Creates a link to a specific help section
 * 
 * @param {string} sectionId - The ID of the help section to link to
 * @param {string} children - The link text (defaults to "Learn more")
 * @param {boolean} showIcon - Whether to show an external link icon (defaults to true)
 * @param {string} variant - Style variant: 'inline', 'button', or 'subtle'
 */
export const HelpLink = ({
  sectionId,
  children = 'Learn more',
  showIcon = true,
  variant = 'inline',
  ...props
}) => {
  const linkColor = useColorModeValue('brand.500', 'brand.400');
  const subtleColor = useColorModeValue('secondaryGray.600', 'secondaryGray.400');

  const getVariantStyles = () => {
    switch (variant) {
      case 'button':
        return {
          px: 3,
          py: 2,
          bg: linkColor,
          color: 'white',
          borderRadius: 'md',
          fontWeight: '500',
          _hover: {
            opacity: 0.8,
            textDecoration: 'none',
          },
        };
      case 'subtle':
        return {
          color: subtleColor,
          fontSize: 'sm',
          textDecoration: 'underline',
          _hover: {
            color: linkColor,
          },
        };
      case 'inline':
      default:
        return {
          color: linkColor,
          textDecoration: 'underline',
          fontWeight: '500',
          _hover: {
            opacity: 0.8,
          },
        };
    }
  };

  return (
    <Link
      href={`/help#${sectionId}`}
      target="_blank"
      rel="noopener noreferrer"
      display="inline-flex"
      alignItems="center"
      gap={1}
      {...getVariantStyles()}
      {...props}
    >
      {children}
      {showIcon && variant !== 'button' && (
        <ExternalLinkIcon boxSize="3" />
      )}
    </Link>
  );
};

/**
 * HelpBadge component - Creates a small badge-style help link
 * Useful for placing next to section headings or labels
 */
export const HelpBadge = ({ sectionId, ...props }) => {
  return (
    <HelpLink
      sectionId={sectionId}
      showIcon={false}
      variant="subtle"
      fontSize="xs"
      {...props}
    >
      ?
    </HelpLink>
  );
};
